import { useState } from "react";
import api from "../api/client";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import SiteRow from "./SiteRow";
import SiteLocationDialog from "./SiteLocationDialog";
import { WORKPLACE_TYPES } from "../lib/workplaceTypes";
import logger from "../lib/logger";

export default function SitesManager({ sites, onChange }) {
  const [newName, setNewName] = useState("");
  const [newType, setNewType] = useState("");
  const [locationSite, setLocationSite] = useState(null);

  const add = async () => {
    const name = newName.trim();
    if (!name) return;
    try {
      await api.post("/sites", { name, type: newType || null });
      setNewName("");
      setNewType("");
      toast.success("Site added");
      onChange();
    } catch (err) {
      logger.error("Add site failed:", err);
      toast.error(err?.response?.data?.detail || "Failed to add");
    }
  };

  const rename = async (id, name) => {
    try {
      await api.patch(`/sites/${id}`, { name });
      toast.success("Saved");
      onChange();
      return true;
    } catch (err) {
      logger.error("Rename site failed:", err);
      toast.error("Failed to save");
      return false;
    }
  };

  const changeType = async (id, type) => {
    try {
      await api.patch(`/sites/${id}`, { type: type || null });
      toast.success(type ? `Type set to ${type}` : "Type cleared");
      onChange();
    } catch (err) {
      logger.error("Change type failed:", err);
      toast.error("Failed to update type");
    }
  };

  const changeClient = async (id, client_name) => {
    try {
      await api.patch(`/sites/${id}`, { client_name });
      toast.success(client_name ? `Client set to ${client_name}` : "Client cleared");
      onChange();
    } catch (err) {
      logger.error("Change client failed:", err);
      toast.error("Failed to update client");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this site and its schedule?")) return;
    try {
      await api.delete(`/sites/${id}`);
      toast.success("Site removed");
      onChange();
    } catch (err) {
      logger.error("Delete site failed:", err);
      toast.error("Failed to delete");
    }
  };

  return (
    <div className="border border-gray-300 bg-white p-5 rounded-sm" data-testid="sites-manager">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-black text-lg tracking-tight">Workplace Sites ({sites.length})</h3>
        <span
          className="text-[10px] uppercase tracking-widest px-2 py-1 bg-[#002FA7] text-white font-bold rounded-sm"
          data-testid="admin-only-badge-sites"
        >
          Admin only
        </span>
      </div>

      <div className="mb-5 space-y-2">
        <div>
          <label className="block text-[10px] uppercase tracking-widest text-gray-500 mb-1">Workplace site</label>
          <div className="flex gap-2">
            <input
              data-testid="new-site-name-input"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="New site name…"
              onKeyDown={(e) => e.key === "Enter" && add()}
              className="flex-1 border border-gray-300 px-3 py-2 rounded-sm focus:border-[#002FA7] outline-none text-sm"
            />
            <button
              onClick={add}
              data-testid="add-site-button"
              className="bg-[#002FA7] text-white px-4 py-2 rounded-sm flex items-center gap-1 hover:bg-[#002277] text-sm font-bold"
            >
              <Plus className="w-4 h-4" /> Add
            </button>
          </div>
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-widest text-gray-500 mb-1">Workplace type</label>
          <select
            data-testid="new-site-type-select"
            value={newType}
            onChange={(e) => setNewType(e.target.value)}
            className="w-full border border-gray-300 px-3 py-2 rounded-sm bg-white text-sm font-medium focus:border-[#002FA7] outline-none"
          >
            <option value="">— Select type (optional) —</option>
            {WORKPLACE_TYPES.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="border-t border-gray-200 pt-3 max-h-[420px] overflow-y-auto -mx-5 px-5">
        {sites.map((s) => (
          <SiteRow
            key={s.id}
            site={s}
            onRename={rename}
            onChangeType={changeType}
            onChangeClient={changeClient}
            onDelete={remove}
            onOpenLocation={setLocationSite}
          />
        ))}
      </div>

      {locationSite && (
        <SiteLocationDialog
          site={locationSite}
          onClose={() => setLocationSite(null)}
          onSaved={onChange}
        />
      )}
    </div>
  );
}
